import isArray from 'lodash/isArray';
import omit from 'lodash/omit';

import { useReactQueryState } from '../../../common/reactQuery/hooks/useReactQueryState';

import {
  FetchMaterialsCacheKey,
  FetchMaterialsFilterFields,
  FetchMaterialsFilters,
  FetchMaterialsSort
} from '../../materialsTypes';

import { INITIAL_MATERIALS_SORT } from '../../materialsConstants';

const defaultIgnoredFilterNames = [
  FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS,
  FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS
];

const pureInitialFilters = { nda: false, blocked: false };

const pureInitialSort = INITIAL_MATERIALS_SORT;

interface MaterialsFiltersCacheOptions {
  cacheKey: FetchMaterialsCacheKey;
  initialFilters?: FetchMaterialsFilters;
  initialSort?: FetchMaterialsSort;
  ignoredFilterNames?: FetchMaterialsFilterFields[];
}

function useMaterialsFiltersCache({
  cacheKey,
  initialFilters = pureInitialFilters,
  initialSort = pureInitialSort,
  ignoredFilterNames = defaultIgnoredFilterNames
}: MaterialsFiltersCacheOptions) {
  const filtersCacheKey = `${cacheKey}-filters`;
  const sortCacheKey = `${cacheKey}-sort`;

  const { value: cachedMaterialsFilters, setValue: setCachedMaterialsFilters } =
    useReactQueryState<FetchMaterialsFilters>(filtersCacheKey, initialFilters);

  const { value: cachedMaterialsSort, setValue: setCachedMaterialsSort } =
    useReactQueryState<FetchMaterialsSort>(sortCacheKey, initialSort);

  return {
    defaultInitialFilters: initialFilters,
    defaultInitialSort: initialSort,
    cachedMaterialsFilters,
    cachedMaterialsSort,
    changeCachedMaterialsFilters: (
      changedFilters: FetchMaterialsFilters,
      removedFilterNames: string[] = []
    ) => {
      setCachedMaterialsFilters(
        omit({ ...cachedMaterialsFilters, ...changedFilters }, [
          ...(isArray(removedFilterNames) ? removedFilterNames : []),
          ...ignoredFilterNames
        ])
      );
    },
    setCachedMaterialsFilters: (nextFilters: FetchMaterialsFilters) => {
      setCachedMaterialsFilters(omit(nextFilters, ignoredFilterNames));
    },
    resetCachedMaterialsFiltersAndSort: (
      onResetFilters?: (initialFilters: FetchMaterialsFilters) => void,
      onResetSort?: (initialSort: FetchMaterialsSort) => void
    ) => {
      setCachedMaterialsFilters(initialFilters);
      onResetFilters?.(initialFilters);
      setCachedMaterialsSort(initialSort);
      onResetSort?.(initialSort);
    },
    setCachedMaterialsSort
  };
}

export default useMaterialsFiltersCache;
